import { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import GoBackHeader from 'components/organisms/GoBackHeader'
import CodeVerification from 'components/molecules/CodeVerification'
import {
  setIsAuthenticationFlag,
  setAuthentication
} from 'reducers/authentication/authenticationSlice'
import {
  UserLoginResponse,
  UserLoginVariables,
  USER_LOGIN
} from 'share/queries/login'
import {
  TFAuthData,
  TFAuthVariables,
  TFAUTH_MUTATION
} from 'share/mutations/authentication'
import {
  AccountCreationStep,
  loadTheReduxStoreWithAuthenticationInfoHelper
} from 'share/utils/utils'
import { AuthVerifyContainer } from 'components/atoms/Layout'
import ErrorMessage from 'components/atoms/ErrorMessage'

const LoginPageTwoFactorAuth: FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const authentication = useAppSelector((state) => state.authentication)

  const [codeValue, setCodeValue] = useState('')
  const [loginData, setLoginData] = useState({} as UserLoginVariables)
  const [hasError, setHasError] = useState(false)

  const [validateMfa, { loading }] =
    useMutation<TFAuthData, TFAuthVariables>(TFAUTH_MUTATION)

  const [doLogin, { data, error, loading: loadingLogin }] = useLazyQuery<
    UserLoginResponse,
    UserLoginVariables
  >(USER_LOGIN, { fetchPolicy: 'no-cache' })

  const loadingDelay = useLoadingDelay(loadingLogin)

  useEffect(() => {
    setLoginData(history.location.state as UserLoginVariables)
  }, [])

  useEffect(() => {
    if (data && data.authentication.tokenFromUsernameAndPassword && !error) {
      const sendAgainAuth = Object.assign({}, authentication)
      const authfromRedux = loadTheReduxStoreWithAuthenticationInfoHelper(data)
      sendAgainAuth.sessionId = authfromRedux.sessionId
      dispatch(setAuthentication(sendAgainAuth))
    }
  }, [data])

  const goToCaseDashboard = (): void => {
    if (!codeValue) return
    if (!authentication) return
    validateMfa({
      variables: {
        code: codeValue,
        user: authentication?.account?.userName,
        sessionId: authentication?.sessionId
      }
    })
      .then((res) => {
        const { data } = res
        if (data && data.authentication.submitMfaCode.idToken) {
          dispatch(setAuthentication(data.authentication.submitMfaCode))
          if (data?.authentication.submitMfaCode.account) {
            if (
              data.authentication.submitMfaCode.account.accountCreationStep ===
              AccountCreationStep.CompletedAllSteps
            ) {
              dispatch(setIsAuthenticationFlag(true))
              if (data?.authentication.submitMfaCode.account.caseCount === 1) {
                history.push('/dashboard/case')
              } else {
                history.push('/client')
              }
            } else {
              // have not yet completed the account creation steps so redirect to account-creation
              history.push('/account-creation/not-completed')
            }
          }
        }
      })
      .catch(() => setHasError(true))
  }

  const handleIncorrectCode = () => {
    setCodeValue('')
    setHasError(false)
    doLogin({ variables: loginData })
  }

  const handleSendAgain = () => {
    setCodeValue('')

    if (loginData) {
      setHasError(false)
      doLogin({ variables: loginData })
    }
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <GoBackHeader text='Two-Factor Authentication' />
      {hasError && (
        <ErrorMessage>
          <span>
            Your verification code is incorrect.{' '}
            <span role='button' onClick={handleIncorrectCode}>
              Request new code.
            </span>
          </span>
        </ErrorMessage>
      )}
      <AuthVerifyContainer>
        <h2 data-testid='login-mfa-title'>Verify your identity.</h2>
        <span className='verify-message-text' data-test-id='login-mfa-message'>
          A verification code has been sent to your mobile number,&nbsp;
          <strong>{authentication.mfaDeliveryDestination}</strong>.{'\n'}
          Message and data rates may apply.
        </span>
        <CodeVerification
          isLoading={loading}
          sendAgain={handleSendAgain}
          timeoutMinutes={3}
          value={codeValue}
          onChange={setCodeValue}
          onClick={goToCaseDashboard}
        />
      </AuthVerifyContainer>
    </>
  )
}

export default LoginPageTwoFactorAuth
